import { addDays, isAfter } from "date-fns";
import { useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import Input from "../../../components/common/Input/Input";
import SelectInput from "../../../components/common/selectInput/selectInput";
import TelInput from "../../../components/common/telInput/telInput";
import TitleDescription from "../../../components/newMotorPolicy/titleDescription/titleDescription";
import useFileClaimFlow from "../../../hooks/useFileClaimFlow";
import useMotorForm from "../../../hooks/useMotorFormData";
import useValidation from "../../../hooks/useValidation";
import "./custom-datepicker.css";
import { FILE_CLAIM_FLOW } from "./data";
import FileClaimMotorLayout from "./fileClaimMotorLayout";
import { PROPERTY_DAMAGE, VEHICLE_DAMAGE } from "./NonSolidarityUser/data";

export const AccidentInfo = "AccidentInfo";

function DatePickerInput({ value, setValue, label, error }) {
  const { t } = useTranslation();

  const isWeekendCustom = (date) => date.getDay() === 5 || date.getDay() === 6;

  const getMinDate = () => {
    let date = addDays(new Date(), 3);
    while (isWeekendCustom(date)) {
      date = addDays(date, 1);
    }
    return date;
  };

  const handleDateChange = (date) => {
    if (
      !date ||
      isWeekendCustom(date) ||
      !isAfter(date, addDays(new Date(), 2))
    ) {
      alert(t("motorInsurance.fileAClaimFlow.accidentInfoPage.date_error"));
      return;
    }
    setValue(date);
  };

  return (
    <div className="formField form-input claim-survey-field">
      {label && (
        <label className="label text-[#48535B] text-base not-italic font-normal leading-[26px] tracking-[-0.16px]">
          {label}
        </label>
      )}
      <div className="w-full relative">
        <DatePicker
          selected={value ? new Date(value) : null}
          onChange={handleDateChange}
          minDate={getMinDate()}
          filterDate={(date) => !isWeekendCustom(date)}
          dateFormat="yyyy-MM-dd"
          placeholderText={t(
            "motorInsurance.fileAClaimFlow.accidentInfoPage.select_date"
          )}
          calendarClassName="z-50 claim-survey-calendar"
          popperClassName="claim-survey-popper"
          popperPlacement="bottom-start"
          wrapperClassName="w-full"
          className={`custom-datepicker-input w-full block h-[50px] justify-center min-h-[50px] items-center gap-2.5 self-stretch px-4 py-3 text-lg not-italic font-normal border rounded-[10px] border-solid ${
            error ? "border-red-500" : "border-[#DDE2E4]"
          }`}
        />
      </div>
      {error && (
        <p className="field-err text-red-500 text-sm mt-1 text-left rtl:text-right">
          {error}
        </p>
      )}
    </div>
  );
}

function AccidentInfoPage({
  garages,
  selectedInsuredType,
  propertyDamageInfo,
}) {
  const [formData, setFormData] = useState({});
  const { handleNextSubstep, flowType } = useFileClaimFlow();

  const { errors, validateFields } = useValidation();
  const { t } = useTranslation();
  const { saveMotorDataInRedux } = useMotorForm(AccidentInfo, setFormData);

  let validationConfig = [
    { name: "accidentDesc", validations: ["required"] },
    { name: "workshop", validations: ["select"] },
    { name: "surveyDate", validations: ["required"] },
  ];

  if (selectedInsuredType == PROPERTY_DAMAGE) {
    validationConfig = [
      { name: "accidentDesc", validations: ["required"] },
      { name: "mobile", validations: ["length"], min: 8 }, // ✅ fix min
      { name: "surveyDate", validations: ["required"] },
    ];
  }

  const validateForm = () => validateFields(validationConfig, formData);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const selectedGarage = garages?.find((g) => g.GarageCode == value);

    if (selectedGarage) {
      setFormData({
        ...formData,
        [name]: selectedGarage.GarageCode,
        Garage: selectedGarage.GarageEnName,
      });
    } else {
      const updated = { ...formData, [name]: value };
      setFormData(updated);
      if (errors?.[name]) validateFields(validationConfig, updated);
    }
  };

  const handleSurveyDateChange = (date) => {
    const isoDate = date?.toISOString?.() ?? "";
    const updated = { ...formData, surveyDate: isoDate };
    setFormData(updated);

    if (errors?.surveyDate) {
      validateFields(validationConfig, updated);
    }
  };

  const continueClickHanlder = () => {
    if (validateForm()) {
      saveMotorDataInRedux(formData);
      handleNextSubstep();
    }
  };

  return (
    <FileClaimMotorLayout btn_onClick={continueClickHanlder}>
      <div className="mx-auto bg-white max767:p-5 w-[500px] max-w-[100%]">
        <div className="max767:hidden">
          <TitleDescription
            title={t("motorInsurance.fileAClaimFlow.accidentInfoPage.title")}
            description={t(
              "motorInsurance.fileAClaimFlow.accidentInfoPage.description"
            )}
          />
        </div>
        <div className="max767:block hidden max767:mb-6">
          <TitleDescription
            title={t(
              "motorInsurance.fileAClaimFlow.accidentInfoPage.mobile_title"
            )}
            description={t(
              "motorInsurance.fileAClaimFlow.accidentInfoPage.mobile_description"
            )}
          />
        </div>

        <Input
          isTextarea
          label={t(
            "motorInsurance.fileAClaimFlow.accidentInfoPage.desc_accident_label"
          )}
          placeHolder={t(
            "motorInsurance.fileAClaimFlow.accidentInfoPage.desc_accident_placeholder"
          )}
          type="text"
          handleChange={handleChange}
          name="accidentDesc"
          value={formData.accidentDesc}
          error={errors.accidentDesc}
          data-testid="accidentDesc"
        />

        {(flowType == FILE_CLAIM_FLOW ||
          selectedInsuredType == VEHICLE_DAMAGE) && (
          <>
            <SelectInput
              className="workshop"
              name="workshop"
              formData={formData}
              value={formData?.workshop}
              label={t(
                "motorInsurance.fileAClaimFlow.accidentInfoPage.preferred_garage_label"
              )}
              default_placeholder={t(
                "motorInsurance.fileAClaimFlow.accidentInfoPage.preferred_garage_placeholder"
              )}
              options_data={garages?.map((garage) => ({
                value: garage.GarageCode,
                label: garage.GarageEnName,
                data: garage,
              }))}
              error={errors?.workshop}
              isSearchable={true}
              handleChange={handleChange}
            />
            <span className="flex note text-[#005574] text-xs not-italic font-normal leading-5 tracking-[-0.12px] mb-4">
              {t(
                "motorInsurance.fileAClaimFlow.accidentInfoPage.preferred_garage_hint_msg"
              )}
            </span>

            <DatePickerInput
              label={t(
                "motorInsurance.fileAClaimFlow.accidentInfoPage.preferred_date_survey_label"
              )}
              value={formData.surveyDate}
              setValue={handleSurveyDateChange}
              error={errors.surveyDate}
            />
          </>
        )}

        {selectedInsuredType == PROPERTY_DAMAGE && (
          <>
            <TelInput
              name="mobile"
              value={formData.mobile}
              error={errors.mobile}
              onChange={handleChange}
              label={t(
                "motorInsurance.fileAClaimFlow.accidentInfoPage.propertyDamageAccidentInfo.contact_person_label"
              )}
              className="mobilenumber px-4 py-3 max767:w-[50%] w-[97%] border-r-0 border-0 rounded-[9px] h-[46px] focus:outline-none"
              data-testid="mobile"
            />
            <DatePickerInput
              label={t(
                "motorInsurance.fileAClaimFlow.accidentInfoPage.propertyDamageAccidentInfo.preferred_date_label"
              )}
              value={formData.surveyDate}
              setValue={handleSurveyDateChange}
              error={errors.surveyDate}
            />
          </>
        )}
      </div>
    </FileClaimMotorLayout>
  );
}

export default AccidentInfoPage;
